<template>
  <div class="page-wrapper">
    <!-- Header -->
    <header class="site-header">
      <div class="site-header-inner">
        <div class="brand">
          <img class="brand-icon" src="/brain-icon-96.jpg" alt="" width="28" height="28" />
          <span class="brand-name">Remote EMDR</span>
        </div>
        <div class="header-actions">
          <router-link class="header-link header-contact" :to="localePath('/guides')">{{ guideLabels.navGuides }}</router-link>
          <router-link class="header-link header-contact" :to="localePath('/about')">{{ guideLabels.navAbout }}</router-link>
          <router-link class="header-link header-contact" :to="localePath('/contact')">{{ $t('nav.contact') }}</router-link>
          <template v-if="!loggedIn">
            <router-link class="header-link" :to="loginPath">{{ $t('nav.login') }}</router-link>
            <router-link class="header-register" :to="registerPath">{{ $t('nav.register') }}</router-link>
          </template>
          <template v-else>
            <router-link class="header-link" :to="dashboardPath">{{ $t('nav.myPatients') }}</router-link>
            <button class="header-link header-logout" @click="doLogout">{{ $t('nav.logout') }}</button>
          </template>
          <language-menu path="/" />
        </div>
      </div>
    </header>

    <main>
    <!-- Hero Section -->
    <section class="hero" aria-labelledby="hero-title">
      <div class="hero-bg"></div>
      <div class="hero-inner">
        <div class="hero-copy">
          <h1 id="hero-title" class="eyebrow">{{ $t('home.hero.h1') }}</h1>
          <p class="hero-headline">{{ $t('home.hero.headline') }}</p>
          <p class="hero-desc">{{ $t('home.hero.desc') }}</p>
          <p v-if="loggedIn" class="hero-welcome">
            {{ $t('home.hero.welcomeBack') }} <strong>{{ userEmail }}</strong>
          </p>
          <div class="hero-actions">
            <button
              v-if="loggedIn"
              class="btn-primary"
              @click="goToDashboard">
              {{ $t('home.hero.ctaDashboard') }}
            </button>
            <button
              :class="['btn-primary', { 'btn-disabled': limitReached, 'btn-secondary': loggedIn }]"
              @click="openTherapist"
              :disabled="limitReached">
              {{ limitReached ? $t('home.hero.ctaLimit') : $t('home.hero.ctaStart') }}
            </button>
            <router-link v-if="!loggedIn" class="btn-primary btn-secondary" :to="registerPath">
              {{ $t('home.hero.ctaRegister') }}
            </router-link>
            <span class="hero-hint">{{ loggedIn ? $t('home.hero.loggedInHint') : $t('home.hero.hint') }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Why register (anonymous visitors only) -->
    <section v-if="!loggedIn" class="register-section" aria-labelledby="register-title">
      <div class="register-inner">
        <h2 id="register-title" class="register-title">{{ $t('home.register.title') }}</h2>
        <p class="register-subtitle">{{ $t('home.register.subtitle') }}</p>
        <ul class="register-grid">
          <li class="register-card" v-for="(item, index) in registerItems" :key="index">
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </li>
        </ul>
        <router-link class="btn-primary register-cta" :to="registerPath">{{ $t('home.register.cta') }}</router-link>
      </div>
    </section>

    <!-- How it works -->
    <section class="how-section" aria-labelledby="how-title">
      <div class="how-inner">
        <h2 id="how-title" class="how-title">{{ $t('home.how.title') }}</h2>
        <ol class="how-steps">
          <li class="how-step" v-for="(step, index) in howSteps" :key="index">
            <span class="how-number">{{ String(index + 1).padStart(2, '0') }}</span>
            <div class="how-step-body">
              <h3>{{ step.title }}</h3>
              <p>{{ step.desc }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <!-- Video walkthrough (VideoObject structured data served by the backend) -->
    <section class="demo-section" aria-labelledby="demo-title">
      <div class="demo-inner">
        <h2 id="demo-title" class="demo-title">{{ $t('home.demo.title') }}</h2>
        <p class="demo-desc">{{ $t('home.demo.desc') }}</p>
        <div class="demo-frame">
          <video
            :key="$locale"
            class="demo-video"
            controls
            playsinline
            preload="none"
            width="1920"
            height="1080"
            :poster="demoPoster"
            :aria-label="$t('home.demo.title')">
            <source :src="demoSrc" type="video/mp4" />
          </video>
        </div>
      </div>
    </section>

    <!-- FAQ (visible content backing the FAQPage structured data served by the backend) -->
    <section class="faq-section" aria-labelledby="faq-title">
      <div class="faq-inner">
        <h2 id="faq-title" class="faq-title">{{ $t('home.faq.title') }}</h2>
        <details class="faq-item" v-for="(item, index) in faqItems" :key="index">
          <summary>{{ item.q }}</summary>
          <p>{{ item.a }}</p>
        </details>
      </div>
    </section>

    <!-- Guides (pages served with pre-rendered content, see GuideView.vue) -->
    <section class="guides-section" aria-labelledby="guides-title">
      <div class="guides-inner">
        <h2 id="guides-title" class="guides-title">{{ $t('home.guides.title') }}</h2>
        <ul class="guides-grid">
          <li class="guides-card" v-for="guide in guides.slice(0, 6)" :key="guide.slug">
            <router-link :to="guidePath(guide.slug)">
              <h3>{{ guide[$locale].title }}</h3>
              <p>{{ guide[$locale].summary }}</p>
            </router-link>
          </li>
        </ul>
        <router-link class="guides-all" :to="localePath('/guides')">{{ guideLabels.allGuides }} →</router-link>
      </div>
    </section>

    <!-- For clients who land here looking for therapy rather than a tool -->
    <section class="booking-band" aria-labelledby="booking-title">
      <div class="booking-inner">
        <div class="booking-copy">
          <h2 id="booking-title">{{ $t('home.booking.title') }}</h2>
          <p>{{ $t('home.booking.text') }}</p>
        </div>
        <a class="btn-primary booking-cta" :href="guideLabels.bookingUrl" target="_blank" rel="noopener">{{ $t('home.booking.cta') }} →</a>
      </div>
    </section>

    <!-- Trust strip -->
    <section class="trust-strip">
      <div class="trust-inner">
        <template v-for="(item, index) in trustItems" :key="index">
          <span>{{ item }}</span>
          <span v-if="index < trustItems.length - 1" class="dot">&middot;</span>
        </template>
      </div>
    </section>
    </main>

    <!-- Footer -->
    <footer class="footer" role="contentinfo">
      <div class="footer-inner">
        <div class="footer-top">
          <div class="footer-brand">
            <div class="footer-logo">
              <img class="footer-logo-icon" src="/brain-icon-96.jpg" alt="" width="36" height="36" />
              <span class="footer-logo-text">Remote EMDR</span>
            </div>
            <p>{{ $t('home.footer.tagline') }}</p>
          </div>
          <nav class="footer-links" aria-label="Resources">
            <h4>{{ $t('home.footer.resourcesTitle') }}</h4>
            <a href="https://www.emdr.com/" target="_blank" rel="noopener noreferrer">{{ $t('home.footer.aboutEmdr') }}</a>
            <a href="https://www.emdr-europe.org/" target="_blank" rel="noopener noreferrer">{{ $t('home.footer.emdrEurope') }}</a>
            <router-link v-for="guide in guides.slice(0, 3)" :key="guide.slug" :to="guidePath(guide.slug)">{{ guide[$locale].title }}</router-link>
            <router-link :to="localePath('/guides')">{{ guideLabels.allGuides }}</router-link>
            <router-link :to="localePath('/contact')">{{ $t('home.footer.contactSupport') }}</router-link>
          </nav>
          <nav class="footer-links" aria-label="Provider">
            <h4>{{ $t('home.footer.providerTitle') }}</h4>
            <router-link :to="localePath('/about')">{{ guideLabels.navAbout }}</router-link>
            <a :href="expatUrl" target="_blank" rel="noopener">{{ $t('home.footer.providerLink') }}</a>
            <a href="mailto:info@expatpsychologie.nl">{{ $t('home.footer.customDev') }}</a>
          </nav>
        </div>
        <div class="footer-bottom">
          <p>
            DRP CONSULTING &middot; KVK: 85650595 &middot; &copy; {{ appVersion }}
            &middot; <router-link class="footer-consent" :to="privacyPath">{{ $t('consent.privacy') }}</router-link>
            &middot; <button class="footer-consent" @click="openConsentSettings">{{ $t('consent.settings') }}</button>
          </p>
          <p class="footer-proud">{{ $t('home.footer.proud') }} <a :href="expatUrl" target="_blank" rel="noopener">ExpatPsychologie.nl</a></p>
        </div>
        <p v-if="writingNote" class="footer-note">{{ writingNote }}</p>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from 'axios';
import { applySeo } from './i18n/head';
import { authState, isLoggedIn, logout } from './auth';
import { openConsentSettings } from './consent';
import { newSessionId } from './sessionId';
import { DEFAULT_LOCALE, localeInfo } from './i18n/locales';
import LanguageMenu from './components/LanguageMenu.vue';

// Written by scripts/demo-video/build.mjs; its version changes whenever the
// videos are re-rendered, so browsers skip their year-long cached copy
import demoVideo from '../public/demo/video.json';
import guideContent from '../public/content/guides.json';

export default {
  components: { LanguageMenu },
  data() {
    return {
      limitReached: false,
      // App version shown in the footer. Format: YYYY.MM.x — bump x for each
      // deploy within the same month; reset x to 1 when the month changes.
      appVersion: '2026.10.3'
    };
  },
  computed: {
    // Only the guides translated into this language
    guides() {
      return guideContent.guides.filter(g => g[this.$locale]);
    },
    guideLabels() {
      return guideContent.labels[this.$locale];
    },
    // Canonical homepage URLs of expatpsychologie.nl, so these backlinks
    // count for the page Google actually indexes
    expatUrl() {
      return this.$t('home.expatUrl');
    },
    howSteps() {
      return this.$t('home.how.steps');
    },
    trustItems() {
      return this.$t('home.trust');
    },
    faqItems() {
      return this.$t('home.faq.items');
    },
    // A language without its own recording yet shows the default one
    demoLocale() {
      return (demoVideo.locales || []).indexOf(this.$locale) !== -1 ? this.$locale : DEFAULT_LOCALE;
    },
    demoSrc() {
      return `/demo/how-to-${this.demoLocale}.mp4?v=${demoVideo.version}`;
    },
    demoPoster() {
      return `/demo/how-to-${this.demoLocale}.jpg?v=${demoVideo.version}`;
    },
    registerItems() {
      return this.$t('home.register.items');
    },
    loggedIn() {
      return !!authState.token && isLoggedIn();
    },
    userEmail() {
      return authState.email;
    },
    loginPath() {
      return this.$localePath('/login');
    },
    privacyPath() {
      return this.localePath('/privacy');
    },
    writingNote() {
      return localeInfo(this.$locale).writingNote;
    },
    registerPath() {
      return { path: this.loginPath, query: { mode: 'register' } };
    },
    dashboardPath() {
      return this.$localePath('/dashboard');
    }
  },
  watch: {
    '$locale'() {
      this.updateSeo();
    }
  },
  created() {
    this.updateSeo();
  },
  mounted() {
    this.checkSessionLimit();
    this.intervalId = setInterval(() => {
      this.checkSessionLimit();
    }, 5000);
  },
  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  },
  methods: {
    guidePath(slug) {
      return this.$localePath(`/guides/${slug}`);
    },
    updateSeo() {
      applySeo({
        locale: this.$locale,
        routeBase: '',
        title: this.$t('home.meta.title'),
        description: this.$t('home.meta.description')
      });
    },
    async checkSessionLimit() {
      try {
        const response = await axios.get('/api/session-count');
        this.limitReached = response.data.limitReached;
      } catch (error) {
        console.error('Error checking session limit:', error);
      }
    },
    openTherapist() {
      if (this.limitReached) return;
      const sessionId = newSessionId();
      window.open(`${this.$localePath('/controller')}?session=${sessionId}`, '_blank');
    },
    openConsentSettings,
    localePath(path) {
      return this.$localePath(path);
    },
    goToDashboard() {
      this.$router.push(this.dashboardPath);
    },
    async doLogout() {
      await logout();
    }
  }
};
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--c-bg);
}

/* ===== HEADER ===== */
.site-header {
  padding: 1.1rem clamp(1.5rem, 5vw, 4rem);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: #16302c;
}
.site-header-inner {
  max-width: 1160px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.brand {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}
.brand-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  object-fit: cover;
}
.brand-name {
  font-family: var(--ff-heading);
  font-size: 1.05rem;
  font-weight: 600;
  color: white;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 1.1rem;
}
.header-link {
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.88rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  transition: color var(--tr-fast);
}
.header-link:hover {
  color: var(--c-accent-light);
}
.header-logout {
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--ff-body);
  padding: 0;
}

/* ===== HERO ===== */
.hero {
  position: relative;
  background: linear-gradient(150deg, #142b27 0%, #1F5B52 55%, #2B6960 100%);
  overflow: hidden;
  padding: clamp(3.5rem, 9vh, 6.5rem) clamp(1.5rem, 5vw, 4rem);
}
.hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse 55% 55% at 85% 15%, rgba(212, 171, 106, 0.16), transparent 60%),
    radial-gradient(ellipse 50% 50% at 5% 95%, rgba(58, 138, 126, 0.35), transparent 60%);
}
.hero-inner {
  position: relative;
  z-index: 1;
  max-width: 720px;
  margin: 0 auto;
  text-align: center;
}
/* The h1: body font and line height so it keeps the look of a small kicker */
.eyebrow {
  font-family: var(--ff-body);
  line-height: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--c-accent-light);
  margin-bottom: 1.1rem;
}
.hero-headline {
  font-family: var(--ff-heading);
  font-size: clamp(2.2rem, 4.2vw, 3.4rem);
  font-weight: 700;
  color: white;
  line-height: 1.15;
  margin-bottom: 1.4rem;
  letter-spacing: -0.01em;
}
.hero-desc {
  color: rgba(255, 255, 255, 0.72);
  font-size: clamp(1rem, 1.4vw, 1.08rem);
  line-height: 1.75;
  max-width: 46ch;
  margin: 0 auto 2.25rem;
}
.hero-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.1rem;
  flex-wrap: wrap;
}
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--c-accent);
  color: #1a2f2c;
  padding: 0.9rem 2.1rem;
  border-radius: 10px;
  border: none;
  font-family: var(--ff-body);
  font-size: 0.98rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--tr-fast);
}
.btn-primary:hover {
  background: var(--c-accent-light);
  transform: translateY(-1px);
}
.btn-disabled {
  background: rgba(255, 255, 255, 0.15) !important;
  color: rgba(255, 255, 255, 0.4) !important;
  cursor: not-allowed !important;
  transform: none !important;
}
.hero-hint {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
}
.hero-welcome {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.92rem;
  margin-bottom: 1.25rem;
}
.hero-welcome strong {
  color: var(--c-accent-light);
}
.btn-secondary {
  background: rgba(255, 255, 255, 0.12);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
}

.header-register {
  padding: 0.4rem 0.95rem;
  border-radius: 50px;
  background: var(--c-accent);
  color: #1a2f2c;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: background var(--tr-fast);
}
.header-register:hover {
  background: var(--c-accent-light);
}
a.btn-primary {
  text-decoration: none;
}

/* ===== WHY REGISTER ===== */
.register-section {
  padding: clamp(3.5rem, 9vh, 6rem) clamp(1.5rem, 5vw, 4rem) 0;
}
.register-inner {
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
}
.register-title {
  font-family: var(--ff-heading);
  font-size: clamp(1.6rem, 3vw, 2rem);
  font-weight: 600;
  color: var(--c-text);
  margin-bottom: 0.6rem;
}
.register-subtitle {
  color: var(--c-text-light);
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
}
.register-grid {
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  text-align: left;
}
.register-card {
  background: white;
  border: 1px solid var(--c-border);
  border-left: 3px solid var(--c-accent);
  border-radius: 12px;
  padding: 1.25rem 1.35rem;
}
.register-card h3 {
  font-family: var(--ff-heading);
  font-size: 1.08rem;
  font-weight: 600;
  color: var(--c-text);
  margin-bottom: 0.35rem;
}
.register-card p {
  color: var(--c-text-light);
  font-size: 0.92rem;
  line-height: 1.6;
}
.register-cta {
  background: var(--c-primary);
  color: white;
}
.register-cta:hover {
  background: var(--c-primary-light);
}

/* ===== FAQ ===== */
.faq-section {
  padding: 0 clamp(1.5rem, 5vw, 4rem) clamp(3.5rem, 9vh, 6rem);
}
.faq-inner {
  max-width: 900px;
  margin: 0 auto;
}
.faq-title {
  font-family: var(--ff-heading);
  font-size: clamp(1.6rem, 3vw, 2rem);
  font-weight: 600;
  color: var(--c-text);
  margin-bottom: 1.5rem;
}
.faq-item {
  border-top: 1px solid var(--c-border);
  padding: 1rem 0;
}
.faq-item:last-child {
  border-bottom: 1px solid var(--c-border);
}
.faq-item summary {
  font-family: var(--ff-heading);
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--c-text);
  cursor: pointer;
}
.faq-item p {
  color: var(--c-text-light);
  font-size: 0.95rem;
  line-height: 1.65;
  padding-top: 0.6rem;
  max-width: 70ch;
}

/* ===== HOW IT WORKS ===== */
.how-section {
  padding: clamp(3.5rem, 9vh, 6rem) clamp(1.5rem, 5vw, 4rem);
}
.how-inner {
  max-width: 900px;
  margin: 0 auto;
}
.how-title {
  font-family: var(--ff-heading);
  font-size: clamp(1.6rem, 3vw, 2rem);
  font-weight: 600;
  color: var(--c-text);
  margin-bottom: 2.25rem;
}
.how-steps {
  list-style: none;
}
.how-step {
  display: flex;
  gap: 1.75rem;
  align-items: flex-start;
  padding: 1.85rem 0;
  border-top: 1px solid var(--c-border);
}
.how-step:last-child {
  border-bottom: 1px solid var(--c-border);
}
.how-number {
  flex-shrink: 0;
  min-width: 2.5ch;
  font-family: var(--ff-heading);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--c-accent);
}
.how-step-body h3 {
  font-family: var(--ff-heading);
  font-size: 1.12rem;
  font-weight: 600;
  color: var(--c-text);
  margin-bottom: 0.4rem;
}
.how-step-body p {
  color: var(--c-text-light);
  font-size: 0.95rem;
  line-height: 1.65;
  max-width: 54ch;
}

/* ===== VIDEO WALKTHROUGH ===== */
.demo-section {
  padding: 0 clamp(1.5rem, 5vw, 4rem) clamp(3.5rem, 9vh, 6rem);
}
.demo-inner {
  max-width: 900px;
  margin: 0 auto;
}
.demo-title {
  font-family: var(--ff-heading);
  font-size: clamp(1.6rem, 3vw, 2rem);
  font-weight: 600;
  color: var(--c-text);
  margin-bottom: 0.6rem;
}
.demo-desc {
  color: var(--c-text-light);
  font-size: 0.95rem;
  line-height: 1.65;
  max-width: 62ch;
  margin-bottom: 1.5rem;
}
.demo-frame {
  border-radius: 16px;
  overflow: hidden;
  background: var(--c-bg-dark);
  box-shadow: 0 18px 50px rgba(20, 43, 39, 0.22);
  aspect-ratio: 16 / 9;
}
.demo-video {
  display: block;
  width: 100%;
  height: 100%;
}

/* ===== GUIDES ===== */
.guides-section {
  padding: 0 clamp(1.5rem, 5vw, 4rem) clamp(3.5rem, 9vh, 6rem);
}
.guides-inner {
  max-width: 900px;
  margin: 0 auto;
}
.guides-title {
  font-family: var(--ff-heading);
  font-size: clamp(1.6rem, 3vw, 2rem);
  font-weight: 600;
  color: var(--c-text);
  margin-bottom: 1.5rem;
}
.guides-grid {
  list-style: none;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}
.guides-card a {
  display: block;
  height: 100%;
  background: white;
  border: 1px solid var(--c-border);
  border-radius: 12px;
  padding: 1.25rem 1.35rem;
  text-decoration: none;
  transition: border-color var(--tr-fast), transform var(--tr-fast);
}
.guides-card a:hover {
  border-color: var(--c-primary);
  transform: translateY(-2px);
}
.guides-card h3 {
  font-family: var(--ff-heading);
  font-size: 1.08rem;
  font-weight: 600;
  color: var(--c-primary);
  margin-bottom: 0.4rem;
}
.guides-card p {
  color: var(--c-text-light);
  font-size: 0.9rem;
  line-height: 1.55;
}
.guides-all {
  display: inline-block;
  margin-top: 1.25rem;
  color: var(--c-primary);
  font-weight: 600;
  text-decoration: none;
}
.guides-all:hover {
  text-decoration: underline;
}

/* ===== BOOKING BAND ===== */
.booking-band {
  background: #16302c;
  padding: clamp(2.5rem, 6vh, 3.5rem) clamp(1.5rem, 5vw, 4rem);
}
.booking-inner {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}
.booking-copy h2 {
  font-family: var(--ff-heading);
  font-size: clamp(1.4rem, 2.6vw, 1.75rem);
  font-weight: 600;
  color: white;
  margin-bottom: 0.5rem;
}
.booking-copy p {
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.96rem;
  line-height: 1.65;
  max-width: 56ch;
}
.booking-cta {
  flex-shrink: 0;
}

/* ===== TRUST STRIP ===== */
.trust-strip {
  background: var(--c-bg-alt);
  border-top: 1px solid var(--c-border);
  border-bottom: 1px solid var(--c-border);
  padding: 1.5rem clamp(1.5rem, 5vw, 4rem);
}
.trust-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem 0.9rem;
  color: var(--c-text-light);
  font-size: 0.85rem;
  font-weight: 500;
}
.trust-inner .dot {
  color: var(--c-border);
}

/* ===== FOOTER ===== */
.footer {
  background: #141F1D;
  color: rgba(255, 255, 255, 0.6);
  padding: 4rem 2rem 2rem;
  margin-top: auto;
}
.footer-inner {
  max-width: 1100px;
  margin: 0 auto;
}
.footer-top {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 3rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.footer-logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.footer-logo-text {
  font-family: var(--ff-heading);
  font-size: 1.15rem;
  font-weight: 600;
  color: white;
}
.footer-brand p {
  font-size: 0.92rem;
  line-height: 1.6;
  max-width: 340px;
}
.footer-links {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.footer-links h4 {
  color: white;
  font-family: var(--ff-body);
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.footer-links a {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.88rem;
  text-decoration: none;
  transition: color var(--tr-fast);
}
.footer-links a:hover {
  color: var(--c-accent-light);
}
.footer-bottom {
  display: flex;
  justify-content: space-between;
  padding-top: 1.5rem;
  font-size: 0.82rem;
}
.footer-bottom a {
  color: var(--c-accent-light);
  text-decoration: none;
}
.footer-consent {
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
}
.footer-consent:hover {
  color: var(--c-accent-light);
}
.footer-proud {
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
}
.footer-note {
  margin-top: 1rem;
  font-size: 0.76rem;
  color: rgba(255, 255, 255, 0.45);
}
.footer-proud a {
  color: var(--c-accent-light);
  font-weight: 600;
  font-style: normal;
}
.footer-logo-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  object-fit: cover;
}

@media (max-width: 768px) {
  .hero {
    padding: 2.5rem 1.25rem 3rem;
  }
  .register-section {
    padding: 2.5rem 1.25rem 0;
  }
  .register-grid {
    grid-template-columns: 1fr;
  }
  .guides-section {
    padding: 0 1.25rem 2.5rem;
  }
  .guides-grid {
    grid-template-columns: 1fr;
  }
  .booking-band {
    padding: 2.5rem 1.25rem;
  }
  .booking-inner {
    flex-direction: column;
    align-items: stretch;
    gap: 1.25rem;
  }
  .register-cta {
    width: 100%;
    justify-content: center;
  }
  .hero-desc {
    max-width: none;
  }
  .btn-primary {
    width: 100%;
    justify-content: center;
    padding: 1rem 2rem;
    font-size: 1.05rem;
  }
  .hero-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
  .hero-hint {
    text-align: center;
  }
  .how-section {
    padding: 2.5rem 1.25rem;
  }
  .how-step {
    gap: 1.1rem;
  }
  .footer {
    padding: 2.5rem 1.25rem 1.5rem;
  }
  .footer-top {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  .footer-bottom {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
  .footer-note {
    text-align: center;
  }
}
/* Phones: contact stays in the footer and the hero carries the register CTA */
/* The header stays on one line in every language. Measured fits for the
   logged-out header: all links from 869px (Dutch, the longest), without
   Guides/About/Contact from 576px. Those three are in the footer too. */
@media (max-width: 900px) {
  .header-contact {
    display: none;
  }
}
@media (max-width: 600px) {
  .header-register {
    display: none;
  }
}
@media (max-width: 420px) {
  .hero-headline {
    font-size: 1.7rem;
  }
}
</style>
